import React, { createContext, useContext, useState, useEffect } from 'react';
import { dbService } from '../services/db';

interface ImageContextType {
  images: Record<string, string>;
  uploadImage: (key: string, file: File) => Promise<boolean>;
  resetImage: (key: string) => Promise<void>; 
  getImage: (key: string, defaultSrc?: string | string[]) => string | string[];
  isProcessing: boolean;
  storageMode: 'local' | 'cloud';
}

const ImageContext = createContext<ImageContextType | undefined>(undefined);

export const useImageContext = () => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImageContext must be used within an ImageProvider');
  }
  return context;
};

const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_SIZE = 1200; // Efficient size for web
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8)); // Slightly higher quality
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

export const ImageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [images, setImages] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [storageMode, setStorageMode] = useState<'local' | 'cloud'>('local');

  useEffect(() => {
    const initStorage = async () => {
      // 1. First, load from local IndexedDB for immediate persistence
      try {
        const localImages = await dbService.getAllImages();
        if (Object.keys(localImages).length > 0) {
          setImages(prev => ({ ...prev, ...localImages }));
        }
      } catch (e) {
        console.error("Local DB load failed", e);
      }

      // 2. Dynamically import Supabase only when needed (Lazy Load)
      try {
        const { supabase, isSupabaseConfigured } = await import('../services/supabase');
        
        if (isSupabaseConfigured()) {
          setStorageMode('cloud');
          
          const { data, error } = await supabase
            .from('site_content')
            .select('*');
          
          if (data) {
            const imgMap: Record<string, string> = {};
            data.forEach((row: any) => {
              imgMap[row.key] = row.data;
              // Sync local DB with what we just got from cloud
              dbService.saveImage(row.key, row.data);
            });
            setImages(prev => ({ ...prev, ...imgMap }));
          }

          // Subscribe to real-time cloud changes
          const channel = supabase
            .channel('site_content_changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_content' }, (payload: any) => {
              if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const { key, data } = payload.new;
                setImages(prev => ({ ...prev, [key]: data }));
                dbService.saveImage(key, data);
              } else if (payload.eventType === 'DELETE') {
                const key = payload.old.key;
                setImages(prev => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });
                dbService.deleteImage(key);
              }
            })
            .subscribe();

          return () => {
            supabase.removeChannel(channel);
          };
        }
      } catch (e) {
        console.error("Cloud sync failed or skipped", e);
      }
    };

    initStorage();
  }, []);

  const uploadImage = async (key: string, file: File): Promise<boolean> => {
    setIsProcessing(true);
    try {
      const base64 = await compressImage(file);
      
      // Always save locally first
      await dbService.saveImage(key, base64);
      setImages(prev => ({ ...prev, [key]: base64 }));

      // Save to cloud if configured (Dynamically imported)
      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
      if (isSupabaseConfigured()) {
        const { error } = await supabase
          .from('site_content')
          .upsert({ key, data: base64 });
        
        if (error) {
          console.warn("Cloud save failed, but local copy is preserved.", error);
        }
      }
      
      return true;
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to process image: " + (error as any).message);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImage = async (key: string) => {
    setIsProcessing(true);
    try {
      // Clear locally
      await dbService.deleteImage(key);
      setImages(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      // Clear cloud
      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
      if (isSupabaseConfigured()) {
        await supabase
          .from('site_content')
          .delete()
          .eq('key', key);
      }
    } catch (error) {
      console.error("Reset error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getImage = (key: string, defaultSrc?: string | string[]) => {
    if (images[key]) return images[key];
    return defaultSrc || '';
  };

  return (
    <ImageContext.Provider value={{ images, uploadImage, resetImage, getImage, isProcessing, storageMode }}>
      {children}
    </ImageContext.Provider>
  );
};